import { reportNumber } from "./reportNumbers";
import { format, isValid, parseISO } from "date-fns";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PillType } from "../components/controls/Pills";

export const updateObject = (oldObject, updatedProperties) => {
  return {
    ...oldObject,
    ...updatedProperties,
  };
};

export const labSampleStatusOptions = [
  { value: 0, label: "Created", type: PillType.Gray },
  { value: 1, label: "Submitted", type: PillType.DarkYellow },
  { value: 3, label: "Hold", type: PillType.Cyan },
  { value: 4, label: "Recall", type: PillType.Success },
  { value: 5, label: "Cancel", type: PillType.Error },
  { value: 6, label: "In process", type: PillType.Gray },
  { value: 7, label: "Delayed", type: PillType.Rose },
  { value: 2, label: "Completed", type: PillType.Success },
];

export const equipmentMaintenanceStatusOptions = [
  { value: 0, label: "Created", type: PillType.Gray },
  { value: 1, label: "Waiting for Repairs", type: PillType.Cyan },
  { value: 2, label: "Under Maintenance", type: PillType.Success },
  { value: 3, label: "Completed", type: PillType.Success },
];

export function convertLabSampleStatus(status) {
  const statusOption = labSampleStatusOptions.filter(
    (el) => el.value === status,
  );

  return statusOption.length === 1
    ? { text: statusOption[0].label, type: statusOption[0].type }
    : {
        text: labSampleStatusOptions[0].label,
        type: labSampleStatusOptions[0].type,
      };
}

export const formatShortDateTime = (date) => {
  if (!date) return "";
  try {
    const parsedDate = parseISO(date);
    if (isValid(parsedDate)) {
      // Example: "Oct 27 6:32 PM" -> "Oct 27 6:32 pm"
      return format(parsedDate, "MMM d h:mm a").replace(/AM|PM/g, (m) =>
        m.toLowerCase(),
      );
    }
  } catch (e) {
    // fall through
  }
  return date;
};

export function isEmployeeUserType() {
  const userType = localStorage.getItem("user_type");
  return userType === "0";
}

export const isFeatureFlag = (flagName) => {
  switch (flagName) {
    case "isQa":
      return localStorage.getItem("qa") === "1";
    default:
      return false;
  }
};

/** Normalize IPM API payloads (start/move status) into the shape ManufacturingRecipeSelect expects. */
export function getInProcessMonitoringStateFromApiResponse(data) {
  if (!data) return null;
  if (data.current_status_record) return data.current_status_record;
  if (data.current_in_process_statuses) return data.current_in_process_statuses;
  if (data.in_process_status_id != null && data.in_process_status_name) {
    return data;
  }
  return null;
}

export function insertItem(array, action) {
  let newArray = array.slice();
  newArray.splice(action.index, 0, action.item);
  return newArray;
}

// needs the index and the item attributes itself
export const updateObjectInArray = (array, action) => {
  return array.map((item, index) => {
    if (index !== action.index) {
      // This isn't the item we care about - keep it as-is
      return item;
    }
    // Otherwise, this is the one we want - return an updated value
    return {
      ...item,
      ...action.item,
    };
  });
};

export const checkValidity = (value, rules) => {
  let isValid = true;
  if (!rules) {
    return true;
  }

  if (rules.required) {
    isValid = value.trim() !== "" && isValid;
  }

  if (rules.minLength) {
    isValid = value.length >= rules.minLength && isValid;
  }

  if (rules.maxLength) {
    isValid = value.length <= rules.maxLength && isValid;
  }

  if (rules.isEmail) {
    const pattern =
      /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
    isValid = pattern.test(value) && isValid;
  }

  if (rules.isNumeric) {
    const pattern = /^\d+$/;
    isValid = pattern.test(value) && isValid;
  }

  return isValid;
};

export const getLastItem = (thePath) =>
  thePath.substring(thePath.lastIndexOf("/") + 1);

export function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();
    return <Component {...props} router={{ location, navigate, params }} />;
  }

  return ComponentWithRouterProp;
}

export const formatDate = (date) => {
  const parsedDate = parseISO(date);
  if (isValid(parsedDate)) {
    return format(parsedDate, "LLL dd, yyyy");
  }
  return date;
};

export const formatDateWithoutParsing = (date) => {
  return format(date, "LLL dd, yyyy");
};

export const formatDateStandard = (date) => {
  const parsedDate = parseISO(date);
  if (isValid(parsedDate)) {
    return format(parsedDate, "yyyy-MM-dd");
  }
  return date;
};

export const formatDateWithTime = (date) => {
  const parsedDate = parseISO(date);
  if (isValid(parsedDate)) {
    return format(parsedDate, "LLL dd, YYY h:mm a");
  }
  return date;
};

export const formatDateWithTimeSeconds = (date) => {
  const parsedDate = parseISO(date);
  if (isValid(parsedDate)) {
    return format(parsedDate, "LLL dd, YYY h:mm:ss a");
  }
  return date;
};

export const convertToDefEventPara = (name, value) => {
  return {
    target: {
      name,
      value,
    },
  };
};

export function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function ingestBarcodeScan(scannedValue) {
  // Handle brackets: (01)84080400311877(11)251124(3102)001868(21)04723190
  // or: (01)99331079087062(3102)002720(13)240611(21)326634194673
  // or without brackets: 0190061741049614112311163102003801216151488150
  // Paraguay format: 100231437223 (12 digits: 8-digit SKU + 4-digit serial)
  // Remove brackets and spaces for consistent parsing
  const cleanedValue = scannedValue.replace(/[()\s]/g, "");

  // Check for Paraguay format (12 digits: 8-digit SKU + 4-digit serial)
  if (cleanedValue.length === 12 && /^\d+$/.test(cleanedValue)) {
    const plu = cleanedValue.substring(0, 8);
    const serial = cleanedValue.substring(8, 12);
    return { plu: plu, serial: serial, date: "", weight: "" };
  }

  // Parse GS1 Application Identifiers dynamically
  let plu = "";
  let date = "";
  let weight = "";
  let weightDecimals = null;
  let serial = "";

  let i = 0;

  // GS1 Application Identifier (AI) definitions and their data lengths
  // AI 01: GTIN - 14 digits
  // AI 11: Production date (YYMMDD) - 6 digits
  // AI 13: Best before date (YYMMDD) - 6 digits
  // AI 3101: Net weight in kg with 2 decimal places - 6 digits
  // AI 3102: Net weight in kg with 2 decimal places - 6 digits
  // AI 3103: Net weight in kg with 3 decimal places - 6 digits
  // AI 21: Serial number - variable length

  while (i < cleanedValue.length) {
    // Check for GTIN (AI 01)
    if (cleanedValue.substring(i, i + 2) === "01" && !plu) {
      plu = cleanedValue.substring(i + 2, i + 16);
      i += 16;
    }
    // Check for Production date (AI 11)
    else if (cleanedValue.substring(i, i + 2) === "11" && !date) {
      date = cleanedValue.substring(i + 2, i + 8);
      i += 8;
    }
    // Check for Best before date (AI 13) - takes precedence over production date if both present
    else if (cleanedValue.substring(i, i + 2) === "13") {
      date = cleanedValue.substring(i + 2, i + 8);
      i += 8;
    }
    // Check for Weight AIs (4-digit codes) - check these BEFORE 2-digit AIs like "21"
    // Weight in kg with 2 decimals (AI 3101)
    else if (cleanedValue.substring(i, i + 4) === "3101" && !weight) {
      const weightValue = cleanedValue.substring(i + 4, i + 10);
      weight = parseInt(weightValue, 10) / 100; // 2 decimal places
      weightDecimals = 2;
      i += 10;
    }
    // Weight in kg with 2 decimals (AI 3102)
    else if (cleanedValue.substring(i, i + 4) === "3102" && !weight) {
      const weightValue = cleanedValue.substring(i + 4, i + 10);
      weight = parseInt(weightValue, 10) / 100; // 2 decimal places
      weightDecimals = 2;
      i += 10;
    }
    // Weight in kg with 3 decimals (AI 3103)
    else if (cleanedValue.substring(i, i + 4) === "3103" && !weight) {
      const weightValue = cleanedValue.substring(i + 4, i + 10);
      weight = parseInt(weightValue, 10) / 1000; // 3 decimal places
      weightDecimals = 3;
      i += 10;
    }
    // Check for Serial number (AI 21) - variable length, goes to end or next AI
    // Must check AFTER 4-digit AIs to avoid false matches
    else if (cleanedValue.substring(i, i + 2) === "21" && !serial) {
      // Serial number continues until the end or until we hit another AI pattern
      // We'll take everything after "21" as serial, up to the end
      serial = cleanedValue.substring(i + 2);

      // Try to detect if there's another AI following (starts with digit, but be careful)
      // For simplicity, we'll take all remaining digits as serial
      // More sophisticated parsing could check for known AI patterns
      i = cleanedValue.length;
    }
    // If we don't recognize the pattern, try to skip past it
    // This handles unknown AIs or malformed data
    else {
      i++;
    }

    // Safety check to prevent infinite loops
    if (i >= cleanedValue.length) break;
  }

  // Convert date from YYMMDD to YYYY-MM-DD format
  if (date && date.length === 6) {
    const year = "20" + date.substring(0, 2);
    const month = date.substring(2, 4);
    const day = date.substring(4, 6);
    date = year + "-" + month + "-" + day;
  } else {
    date = "";
  }

  // Fallback: If using old format and we didn't find anything, try old method
  // This maintains backward compatibility
  if (!plu && cleanedValue.length >= 16) {
    plu = cleanedValue.substring(2, 16);
    if (cleanedValue.length >= 24) {
      date = cleanedValue.substring(18, 24);
      if (date && date.length === 6) {
        const year = "20" + date.substring(0, 2);
        const month = date.substring(2, 4);
        const day = date.substring(4, 6);
        date = year + "-" + month + "-" + day;
      }
    }
    if (cleanedValue.length >= 34) {
      weight = cleanedValue.substring(28, 34);
      if (weight) {
        if (weight.substring(0, 2) != "00") {
          weight = parseInt(weight, 10) / 10000;
        } else {
          weight = parseInt(weight, 10) / 100;
        }
      }
    }
    if (cleanedValue.length > 36) {
      serial = cleanedValue.substring(36);
    }
  }

  // Format weight with appropriate decimal places if we have weightDecimals info
  let formattedWeight = weight;
  if (weight !== "" && weightDecimals !== null) {
    formattedWeight = parseFloat(weight).toFixed(weightDecimals);
  }

  const info = {
    plu: plu,
    date: date,
    weight: formattedWeight,
    weightDecimals: weightDecimals,
    serial: serial,
  };
  return info;
}

export function convertToTwoDecimals(valToConvert) {
  return valToConvert.toFixed(2);
}

export const orderStatusOptions = [
  { label: "All statuses", value: "all" },
  { label: "Draft", value: "draft", type: PillType.Gray },
  { label: "Created", value: "created", type: PillType.Warning },
  { label: "Processing", value: "processing", type: PillType.Orange },
  { label: "Picking in progress", value: "picking", type: PillType.LightLime },
  {
    label: "Waiting for shipment",
    value: "waiting_for_shipment",
    type: PillType.Cyan,
  },
  { label: "Shipped", value: "shipped", type: PillType.DarkSky },
  { label: "Completed", value: "completed", type: PillType.Success },
  { label: "Pending payment", value: "pending_payment", type: PillType.Rose },
  {
    label: "Ready for pickup",
    value: "ready_for_pickup",
    type: PillType.DarkYellow,
  },
  { label: "On hold", value: "on_hold", type: PillType.Error },
  { label: "Refunded", value: "refunded", type: PillType.Error },
  { label: "Cancelled", value: "cancelled", type: PillType.Error },
  {
    label: "Backordered",
    value: "backordered",
    type: PillType.Backordered,
  },
];

export const shipmentStatusOptions = [
  { label: "Draft", value: "draft", type: PillType.Gray },
  { label: "Picking", value: "picking", type: PillType.LightLime },
  { label: "Picked", value: "picked", type: PillType.Cyan },
  { label: "Loaded", value: "loaded", type: PillType.Orange },
  { label: "Shipped", value: "shipped", type: PillType.DarkSky },
  { label: "Delivered", value: "delivered", type: PillType.Success },
  { label: "Voided", value: "voided", type: PillType.Error },
];

export function normalizeShipmentStatus(status, fallback = "draft") {
  const value =
    status != null && typeof status === "object" ? status.value : status;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();

  return shipmentStatusOptions.some((option) => option.value === normalized)
    ? normalized
    : fallback;
}

function shipmentStatusLookupKey(value) {
  return String(value)
    .replaceAll("_", " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Pill content for a shipment status value, option object, or label. */
export function shipmentStatusPill(status) {
  const candidates = [];
  if (status != null && typeof status === "object") {
    if (status.value != null && status.value !== "")
      candidates.push(status.value);
    if (status.label != null && status.label !== "")
      candidates.push(status.label);
    if (status.text != null && status.text !== "") candidates.push(status.text);
  } else if (status != null && status !== "") {
    candidates.push(status);
  }

  const statuses = shipmentStatusOptions;

  for (const candidate of candidates) {
    const key = shipmentStatusLookupKey(candidate);
    const match = statuses.find(
      (option) =>
        shipmentStatusLookupKey(option.label) === key ||
        shipmentStatusLookupKey(option.value) === key,
    );
    if (match) return { text: match.label, type: match.type };
  }

  const rawText =
    status != null && typeof status === "object"
      ? (status.label ?? status.text ?? candidates[0] ?? "")
      : (status ?? "");

  return {
    text: String(rawText).replaceAll("_", " ").trim(),
    type: PillType.Gray,
  };
}

export const employeeTypesOptions = [
  { label: "Regular", value: 1 },
  { label: "Supervisor", value: 2 },
];

export const employeeDepartmentsOptions = [
  { label: "All", value: 1 },
  { label: "Packaging", value: 2 },
  { label: "Production", value: 3 },
  { label: "Receiving", value: 4 },
  { label: "Shipping", value: 5 },
  { label: "Orders", value: 6 },
];

/**
 * Format a number as USD currency (e.g. 40083.65 -> "$40,083.65").
 * Missing or non-finite amounts display as "$0.00".
 */
export function formatCurrency(value) {
  const num = reportNumber(value);
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

/** Round a number to 2 decimal places for currency storage. Returns null for non-finite values. */
export function roundCurrency(n) {
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
}

export const yesOrNoOptions = [
  { label: "N/A", value: 0 },
  { label: "Yes", value: 1 },
  { label: "No", value: 2 },
];

export const productionStatusOptions = [
  {
    value: "0",
    label: "Created",
    type: PillType.Orange,
  },
  {
    value: "1",
    label: "In progress",
    type: PillType.Gray,
  },
  {
    value: "2",
    label: "Completed",
    type: PillType.Success,
  },
];

export function generateUniqueNumber() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0"); // getMonth() returns 0-11, so add 1
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  const uniqueNumber = `${month}${day}${hours}${minutes}${seconds}`;
  return uniqueNumber.slice(-10); // Ensure it's 10 digits
}

/**
 * Build ZPL for your label (ported from your Python script).
 * Returns a single ZPL string you can send to a printer.
 */
export function buildLabelZpl(dataToPrint) {
  const {
    brand,
    plu,
    name,
    description,
    ingredients,
    net_weight,
    production_date,
    best_before_date,
    lot_number,
    barcode,
    cert_1,
    cert_2,
    logo,
    address,
    case_barcode,
    storage_description_en,
  } = dataToPrint;

  const nameZpl = `^CF0,40\n^FO630,275^FD${name ?? ""}^FS\n`;

  // Process description field - split after first ) if there's more text
  let descriptionZpl = "";
  const desc = String(description ?? "");
  const firstParenIndex = desc.indexOf(")");
  if (firstParenIndex !== -1 && firstParenIndex < desc.length - 1) {
    // Split after the first closing parenthesis
    const firstPart = desc.substring(0, firstParenIndex + 1); // Include the closing parenthesis
    const secondPart = desc.substring(firstParenIndex + 1); // Everything after )

    // Only split if there's actual content after the )
    if (secondPart.trim()) {
      // First line at position 600,275
      descriptionZpl = `^CF0,30\n^FO600,275^FD${firstPart}^FS\n`;
      // Second line at position 570,275 (20 pixels below)
      descriptionZpl += `^CF0,30\n^FO570,275^FD${secondPart}^FS\n`;
    } else {
      // Single line format if no content after )
      descriptionZpl = `^CF0,30\n^FO600,275^FD${desc}^FS\n`;
    }
  } else {
    // Single line format
    descriptionZpl = `^CF0,30\n^FO600,275^FD${desc}^FS\n`;
  }

  // --- Text wrapping like Python's textwrap.wrap ---
  const maxLineLength = 65; // approx chars per line (same as Python)
  const wrapText = (text, width) => {
    const words = String(text ?? "")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ");
    const lines = [];
    let line = "";

    for (const w of words) {
      if (!w) continue;
      const candidate = line ? `${line} ${w}` : w;
      if (candidate.length <= width) {
        line = candidate;
      } else {
        if (line) lines.push(line);
        // if a single word is longer than width, hard-split it
        if (w.length > width) {
          let i = 0;
          while (i < w.length) {
            lines.push(w.slice(i, i + width));
            i += width;
          }
          line = "";
        } else {
          line = w;
        }
      }
    }
    if (line) lines.push(line);
    return lines;
  };

  const ingredientsLines = wrapText(
    `Ingredients: ${ingredients}`,
    maxLineLength,
  );

  let ingredientsZpl = "";
  const yStart = 540;
  const x = 275;
  const lineSpacing = 20;

  for (let i = 0; i < ingredientsLines.length; i++) {
    const line = ingredientsLines[i];
    const y = yStart - i * lineSpacing;
    ingredientsZpl += `^CF0,22\n^FO${y},${x}^FD${line}^FS\n`;
  }

  // Conditionally include case barcode ZPL code
  let caseBarcodeZpl = "";
  if (case_barcode && case_barcode.trim()) {
    caseBarcodeZpl = `
^CF0,30
^FO675,900^FDUPC Barcode^FS

^FO110,900
^BY3,2,80
^BCN,,Y,N
^FD${case_barcode}^FS
`;
  }

  const zpl = `
^XA

${logo ?? ""}

^FWR
^CF0,50
^FO660,275^FD${brand ?? ""}^FS

${nameZpl}
${descriptionZpl}

${ingredientsZpl}

${cert_1 ?? ""}
${cert_2 ?? ""}

^CF0,55
^FO430,440^FDNET WT: ${net_weight ?? ""}^FS

^FWR
^CF0,60
^FO630,40^FD${plu ?? ""}^FS

^FWR
^CF0,25
^FO395,40^FDProduction date^FS

^FWR
^CF0,25
^FO375,40^FDDate de production^FS

^FWR
^CF0,25
^FO355,40^FD${production_date ?? ""}^FS

^FWR
^CF0,25
^FO395,240^FDBest before^FS

^FWR
^CF0,25
^FO375,240^FDMeilleure avant^FS

^FWR
^CF0,25
^FO355,240^FD${best_before_date ?? ""}^FS

^FWR
^CF0,25
^FO395,440^FD _____ ^FS

^FWR
^CF0,25
^FO375,440^FD${storage_description_en ?? ""}^FS

^FWR
^CF0,25
^FO355,440^FDLot #: ${lot_number ?? ""}^FS

^FO235, 60 ^BY 2 ^BC , 110, , , , A ^FD ${barcode ?? ""} ^FS

^CF0,20
^FO165,40^FD${address ?? ""}^FS

^CF0,20
^FO145,40^FDCreated by: QaTrace.com^FS

${caseBarcodeZpl}
^XZ
`.trim();

  return zpl;
}

export const conditionTypes = [
  { value: 0, label: "Choose Condition" },
  { value: 1, label: "Satisfactory" },
  { value: 2, label: "NOT Satisfactory" },
];

export const productConditionOptions = conditionTypes.filter(
  (option) => option.value === 1 || option.value === 2,
);

export function lineProductConditionOption(condition) {
  const raw =
    condition != null && typeof condition === "object"
      ? condition.value
      : condition;
  const value = Number(raw);
  if (value !== 1 && value !== 2) return null;
  return (
    productConditionOptions.find((option) => option.value === value) ?? null
  );
}

export const convertPayloadToFormData = (payload) => {
  const formData = new FormData();

  Object.keys(payload).forEach((key) => {
    const value = payload[key];

    if (value === null || value === undefined) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item instanceof File) {
          formData.append(key, item);
        } else if (
          item &&
          typeof item === "object" &&
          !(item instanceof Date)
        ) {
          Object.keys(item).forEach((nestedKey) => {
            formData.append(`${key}[${index}][${nestedKey}]`, item[nestedKey]);
          });
        } else {
          formData.append(`${key}[${index}]`, item);
        }
      });
    } else if (value instanceof File) {
      formData.append(key, value);
    } else if (value && typeof value === "object" && !(value instanceof Date)) {
      Object.keys(value).forEach((nestedKey) => {
        formData.append(`${key}[${nestedKey}]`, value[nestedKey]);
      });
    } else {
      formData.append(key, value);
    }
  });

  return formData;
};
